import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Checkbox,
  Form,
  Input,
  Radio,
  Select,
  DatePicker,
  Button,
  Row,
  Col,
  Spin,
  Space,
  Typography,
  Empty,
  Modal,
} from 'antd';
import { MessageOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import ProductImagesFormField from '../../../components/productImagesForField/ProductImagesFormField';
import ReadOnlyField from '../../../components/readOnlyField/ReadOnlyField';
import OrderSendToQcModal from '../send-to-qc/OrderSendToQcModal';
import OrderDispatchConfirmModal from '../dispatch-confirm/OrderDispatchConfirmModal';
import OrderCompleteConfirmModal from '../complete-confirm/OrderCompleteConfirmModal';
import { editOrderSchema, validateOrderForm } from '../../../utils/orderValidation';
import { linkifyText } from '../../../utils/linkify';
import CommentAttachments from '../../../components/CommentAttachments/CommentAttachments';
import CommentAttachmentInput from '../../../components/CommentAttachments/CommentAttachmentInput';
import './OrderEdit.css';
import { canQcEditOnly, isQcManager as isQcManagerRole } from '../../../utils/userRoles';

const SOURCE_OPTIONS = [
  { value: 'K Etsy', label: 'K Etsy' },
  { value: 'Anirudh Etsy', label: 'Anirudh Etsy' },
  { value: 'Website', label: 'Website' },
  { value: 'Others', label: 'Others' },
];  

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'pending', label: 'Pending' },
  { value: 'ready', label: 'Ready (send to QC)' },
  { value: 'repair', label: 'Repair' },
  { value: 'dispatch', label: 'Dispatch' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'return', label: 'Return' },
];

const ORDER_READY_OPTIONS = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
  { value: 'Incomplete', label: 'Incomplete' },
];

const MODE_OF_PARCEL_OPTIONS = [
  { value: 'OnPoint', label: 'OnPoint' },
  { value: 'Sardar', label: 'Sardar' },
  { value: 'Others', label: 'Others' },
];

const MARKING_OPTIONS = [
  { value: 'No', label: 'No' },
  { value: '14K', label: '14K' },
  { value: '18K', label: '18K' },
  { value: '10K', label: '10K' },
  { value: '9K', label: '9K' },
  { value: '925', label: '925' },
];

export default function OrderEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false); 
  const [commentText, setCommentText] = useState('');
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [commentLoading, setCommentLoading] = useState(false);
  const chatMessagesRef = useRef(null);
  const { user: currentUser } = useSelector((state) => state.authReducer);
  const isAdmin = currentUser?.roles?.some((r) => r.value === 'admin');
  const isQcOnly = canQcEditOnly(currentUser);
  const isQcManager = isQcManagerRole(currentUser);
  const isOrderOrStoneManagement = currentUser?.roles?.some(
    (r) => r.value === 'order-manager' || r.value === 'stone-manager'
  );
  const isStockManager = currentUser?.roles?.some((r) => r.value === 'stock-manager');

  const [qcConfirmModalOpen, setQcConfirmModalOpen] = useState(false);
  const [dispatchConfirmModalOpen, setDispatchConfirmModalOpen] = useState(false);
  const [completeConfirmModalOpen, setCompleteConfirmModalOpen] = useState(false);
  const [repairConfirmModalOpen, setRepairConfirmModalOpen] = useState(false);
  const [pendingSubmitValues, setPendingSubmitValues] = useState(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    axios
      .get(`/orders/${id}`)
      .then((res) => {
        if (!cancelled) setOrder(res.data);
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = err.response?.data?.error || 'Failed to load order';
          dispatch(setMessage({ message: msg, type: 'error' }));
          navigate('/orders');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, navigate, dispatch]);

  useEffect(() => {
    if (!order) return;
    const productImages = Array.isArray(order.productImages) ? order.productImages : [];
    const parcelImages = Array.isArray(order.parcelImages) ? order.parcelImages : [];
    const marking = Array.isArray(order.marking) ? order.marking : [];
    form.setFieldsValue({
      customerName: order.customerName ?? '',
      orderDate: order.orderDate ? dayjs(order.orderDate) : null,
      targetDate: order.targetDate ? dayjs(order.targetDate) : null,
      orderNumber: order.orderNumber ?? '',
      sourceOfOrder: order.sourceOfOrder ?? undefined,
      status: order.status ?? 'open',
      modeOfParcel: order.modeOfParcel ?? '',
      envelopeReady: order.envelopeReady === true,
      orderReady: order.orderReady ?? 'Incomplete',
      stonePrepared: order.stonePrepared === true,
      productImages,
      parcelImages,
      courierName: order.courierName ?? '',
      trackingNumber: order.trackingNumber ?? '',
      qualityGhatTheek: order.qualityGhatTheek === true,
      qualityStoneSahi: order.qualityStoneSahi === true,
      qualityOilLaga: order.qualityOilLaga === true,
      qualitySettingTheek: order.qualitySettingTheek === true,
      qualityNoScratch: order.qualityNoScratch === true,
      qualityPolishTheek: order.qualityPolishTheek === true,
      qualityRhodiumTheek: order.qualityRhodiumTheek === true,
      qualityEarringPush: order.qualityEarringPush === true,
      qualityLockBand: order.qualityLockBand === true,
      ringSize: order.ringSize ?? '',
      samaanGinti: order.samaanGinti ?? '',
      marking,
    });
  }, [order, form]);

  const handleAddComment = async () => {
    const trimmed = commentText?.trim();
    if (!trimmed) return;
    setCommentLoading(true);
    try {
      const res = await axios.post(`/orders/${id}/comments`, {
        text: trimmed,
        attachments: commentAttachments,
      });
      setOrder(res.data);
      setCommentText('');
      setCommentAttachments([]);
      dispatch(setMessage({ message: 'Comment added', type: 'success' }));
      setTimeout(() => { if (chatMessagesRef.current) chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight; }, 100);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to add comment';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setCommentLoading(false);
    }
  };

  const buildPayload = (values, statusOverride = null) => ({
    customerName: values.customerName?.trim() ?? '',
    orderDate: values.orderDate ? dayjs(values.orderDate).toISOString() : null,
    targetDate: values.targetDate ? dayjs(values.targetDate).toISOString() : null,
    orderNumber: values.orderNumber?.trim() ?? '',
    sourceOfOrder: values.sourceOfOrder,
    status: statusOverride ?? values.status ?? 'open',
    modeOfParcel: values.modeOfParcel?.trim() ?? '',
    envelopeReady: values.envelopeReady === true,
    orderReady: values.orderReady ?? 'Incomplete',
    stonePrepared: values.stonePrepared === true,
    productImages: Array.isArray(values.productImages) ? values.productImages : [],
    parcelImages: Array.isArray(values.parcelImages) ? values.parcelImages : [],
    courierName: values.courierName?.trim() ?? '',
    trackingNumber: values.trackingNumber?.trim() ?? '',
    qualityGhatTheek: values.qualityGhatTheek === true,
    qualityStoneSahi: values.qualityStoneSahi === true,
    qualityOilLaga: values.qualityOilLaga === true,
    qualitySettingTheek: values.qualitySettingTheek === true,
    qualityNoScratch: values.qualityNoScratch === true,
    qualityPolishTheek: values.qualityPolishTheek === true,
    qualityRhodiumTheek: values.qualityRhodiumTheek === true,
    qualityEarringPush: values.qualityEarringPush === true,
    qualityLockBand: values.qualityLockBand === true,
    ringSize: values.ringSize?.trim() ?? '',
    samaanGinti: values.samaanGinti?.trim() ?? '',
    marking: Array.isArray(values.marking) ? values.marking : [],
  });

  const doSubmit = async (values, statusOverride = null) => {
    setSubmitLoading(true);
    try {
      await axios.put(`/orders/${id}`, buildPayload(values, statusOverride));
      dispatch(setMessage({ message: 'Order updated successfully', type: 'success' }));
      navigate('/orders');
    } catch (err) {
      const msg = err.response?.data?.error || 'Update failed';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setSubmitLoading(false);
      setPendingSubmitValues(null);
    }
  };

  const runValidation = async (valuesOverride) => {
    const values = valuesOverride ?? form.getFieldsValue();
    const fieldNames = Object.keys(editOrderSchema.fields);
    const fieldErrors = await validateOrderForm(editOrderSchema, values);
    if (fieldErrors) {
      const errorNames = new Set(fieldErrors.map((e) => e.name));
      const clearValid = fieldNames.filter((n) => !errorNames.has(n)).map((name) => ({ name, errors: [] }));
      form.setFields([...fieldErrors, ...clearValid]);
    } else {
      form.setFields(fieldNames.map((name) => ({ name, errors: [] })));
    }
  };

  const handleSaveClick = async (values) => {
    const fieldErrors = await validateOrderForm(editOrderSchema, values);
    if (fieldErrors) {
      form.setFields(fieldErrors);
      dispatch(setMessage({ message: 'Please fix the form errors', type: 'warning' }));
      return;
    }

    const currentStatus = order?.status;

    const showCompleteModal = isStockManager && currentStatus === 'dispatch';
    if (showCompleteModal) {
      setPendingSubmitValues(values);
      setCompleteConfirmModalOpen(true);
      return;
    }

    const showDispatchModal = isQcManager && currentStatus === 'ready';
    if (showDispatchModal) {
      setPendingSubmitValues(values);
      setDispatchConfirmModalOpen(true);
      return;
    }

    const showQcModal =
      isOrderOrStoneManagement &&
      (currentStatus === 'open' || currentStatus === 'pending');
    if (showQcModal) {
      setPendingSubmitValues(values);
      setQcConfirmModalOpen(true);
      return;
    }

    doSubmit(values);
  };

  const handleQcConfirmYes = () => {
    if (!pendingSubmitValues) return;
    setQcConfirmModalOpen(false);
    doSubmit(pendingSubmitValues, 'ready');
  };

  const handleQcConfirmNo = () => {
    if (!pendingSubmitValues) return;
    setQcConfirmModalOpen(false);
    doSubmit(pendingSubmitValues, 'pending');
  };

  const handleDispatchConfirmYes = () => {
    if (!pendingSubmitValues) return;
    setDispatchConfirmModalOpen(false);
    doSubmit(pendingSubmitValues, 'dispatch');
  };

  const handleDispatchConfirmNo = () => {
    if (!pendingSubmitValues) return;
    setDispatchConfirmModalOpen(false);
    doSubmit(pendingSubmitValues, 'ready');
  };

  const handleCompleteConfirmYes = () => {
    if (!pendingSubmitValues) return;
    setCompleteConfirmModalOpen(false);
    doSubmit(pendingSubmitValues, 'completed');
  };

  const handleCompleteConfirmNo = () => {
    if (!pendingSubmitValues) return;
    setCompleteConfirmModalOpen(false);
    doSubmit(pendingSubmitValues, 'dispatch');
  };

  const handleSendToRepair = () => {
    setRepairConfirmModalOpen(true);
  };

  const handleRepairConfirmYes = () => {
    setRepairConfirmModalOpen(false);
    doSubmit(form.getFieldsValue(), 'repair');
  };

  const handleRepairConfirmNo = () => {
    setRepairConfirmModalOpen(false);
  };

  const handleRepairDone = () => {
    doSubmit(form.getFieldsValue(), 'pending');
  };

  if (loading) {
    return (
      <div className="order-edit-page">
        <div className="order-edit-loading">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!order) return null;

  const { Title } = Typography;

  return (
    <div className="order-edit-page">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div className="order-edit-header">
          <div className="order-edit-header-left">
            <Title level={3} style={{ margin: 0 }}>
              Edit order
            </Title>
            {order.orderNumber && (
              <p className="order-edit-subtitle">Order #{order.orderNumber}</p>
            )}
          </div>
          <Space>
            {['pending', 'ready', 'dispatch'].includes(order?.status) && (
              <Button
                danger
                size="large"
                loading={submitLoading}
                onClick={handleSendToRepair}
              >
                Send to Repair
              </Button>
            )}
            {order?.status === 'repair' && (
              <Button
                type="primary"
                size="large"
                loading={submitLoading}
                onClick={handleRepairDone}
                style={{ background: '#52c41a', borderColor: '#52c41a' }}
              >
                Repair Done
              </Button>
            )}
            <Button
              type="primary"
              size="large"
              loading={submitLoading}
              onClick={() => form.submit()}
              className="app-btn-primary"
            >
              Save changes
            </Button>
            <Button onClick={() => navigate('/orders')} size="large" className="app-btn-cancel">
              Cancel
            </Button>
          </Space>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveClick}
          onValuesChange={(_changed, allValues) => setTimeout(() => runValidation(allValues), 0)}
          onFinishFailed={() => dispatch(setMessage({ message: 'Please fix the form errors', type: 'warning' }))}
          className="order-edit-form"
        >
        <Card className="order-edit-card">
            <div className="order-edit-body">
              <Row gutter={16}>
                <Col xs={24}>
                  <Form.Item name="productImages" label="Product images">
                    <ProductImagesFormField disabled={isQcOnly} className="order-edit-upload-large" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="customerName" label="Customer name" required>
                    {isQcOnly ? (
                      <ReadOnlyField placeholder="Customer name" />
                    ) : (
                      <Input placeholder="Customer name" maxLength={200} />
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="orderNumber" label="Order number" required>
                    {isQcOnly ? (
                      <ReadOnlyField placeholder="Order number" />
                    ) : (
                      <Input placeholder="Order number" maxLength={100} />
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="orderDate" label="Order date" required>
                    {isQcOnly ? (
                      <ReadOnlyField placeholder="Order date" format={(v) => (v ? dayjs(v).format('YYYY-MM-DD') : '')} />
                    ) : (
                      <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="targetDate" label="Target date" required>
                    {isQcOnly ? (
                      <ReadOnlyField placeholder="Target date" format={(v) => (v ? dayjs(v).format('YYYY-MM-DD') : '')} />
                    ) : (
                      <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="sourceOfOrder" label="Source of order" required>
                    {isQcOnly ? (
                      <ReadOnlyField placeholder="Select source" options={SOURCE_OPTIONS} />
                    ) : (
                      <Select placeholder="Select source" options={SOURCE_OPTIONS} allowClear={false} />
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="modeOfParcel" label="Mode of parcel">
                    {isQcOnly ? (
                      <ReadOnlyField placeholder="Select mode" options={MODE_OF_PARCEL_OPTIONS} />
                    ) : (
                      <Select placeholder="Select mode" options={MODE_OF_PARCEL_OPTIONS} allowClear style={{ width: '100%' }} />
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="status" label="Status">
                    {isAdmin ? (
                      <Select placeholder="Status" options={STATUS_OPTIONS} />
                    ) : (
                      <ReadOnlyField placeholder="Status" options={STATUS_OPTIONS} />
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="envelopeReady" label="Envelope ready">
                    {isQcOnly ? (
                      <ReadOnlyField placeholder="—" format={(v) => (v === true ? 'Yes' : v === false ? 'No' : '')} />
                    ) : (
                      <Radio.Group
                        options={[
                          { value: true, label: 'Yes' },
                          { value: false, label: 'No' },
                        ]}
                      />
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="stonePrepared" label="Stone prepared">
                    {isQcOnly ? (
                      <ReadOnlyField placeholder="—" format={(v) => (v === true ? 'Yes' : v === false ? 'No' : '')} />
                    ) : (
                      <Radio.Group
                        options={[
                          { value: true, label: 'Yes' },
                          { value: false, label: 'No' },
                        ]}
                      />
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="orderReady" label="Order ready">
                    {isQcOnly ? (
                      <ReadOnlyField placeholder="Select" options={ORDER_READY_OPTIONS} />
                    ) : (
                      <Select placeholder="Select" options={ORDER_READY_OPTIONS} />
                    )}
                  </Form.Item>
                </Col>
                
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="courierName" label="Courier name">
                    {isQcOnly ? (
                      <ReadOnlyField placeholder="Enter courier name" />
                    ) : (
                      <Input placeholder="Enter courier name" maxLength={200} />
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="trackingNumber" label="Tracking number">
                    {isQcOnly ? (
                      <ReadOnlyField placeholder="Enter tracking number" />
                    ) : (
                      <Input placeholder="Enter tracking number" maxLength={200} />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </div>
        </Card>

        <Card title="Quality check" className="order-edit-card order-edit-quality-card">
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <div className="order-edit-quality-checkboxes">
                <Form.Item name="qualityGhatTheek" valuePropName="checked" style={{ marginBottom: 12 }}>
                  <Checkbox>घाट ठीक है</Checkbox>
                </Form.Item>
                <Form.Item name="qualityStoneSahi" valuePropName="checked" style={{ marginBottom: 12 }}>
                  <Checkbox>स्टोन सही है</Checkbox>
                </Form.Item>
                <Form.Item name="qualityOilLaga" valuePropName="checked" style={{ marginBottom: 12 }}>
                  <Checkbox>ऑयल लगा दिया</Checkbox>
                </Form.Item>
                <Form.Item name="qualitySettingTheek" valuePropName="checked" style={{ marginBottom: 12 }}>
                  <Checkbox>सेटिंग ठीक है</Checkbox>
                </Form.Item>
                <Form.Item name="qualityNoScratch" valuePropName="checked" style={{ marginBottom: 12 }}>
                  <Checkbox>कोई स्क्रैच नहीं है</Checkbox>
                </Form.Item>
                <Form.Item name="qualityPolishTheek" valuePropName="checked" style={{ marginBottom: 12 }}>
                  <Checkbox>पॉलिश ठीक है</Checkbox>
                </Form.Item>
                <Form.Item name="qualityRhodiumTheek" valuePropName="checked" style={{ marginBottom: 12 }}>
                  <Checkbox>रोडियम ठीक है</Checkbox>
                </Form.Item>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div className="order-edit-quality-checkboxes">
                <Form.Item name="qualityEarringPush" valuePropName="checked" style={{ marginBottom: 12 }}>
                  <Checkbox>इयररिंग का पुश सही काम कर रहा है</Checkbox>
                </Form.Item>
                <Form.Item name="qualityLockBand" valuePropName="checked" style={{ marginBottom: 12 }}>
                  <Checkbox>लॉक सही से बंद हो रहा है</Checkbox>
                </Form.Item>
              </div>
              <div style={{ marginTop: 16 }}>
                <Form.Item name="ringSize" label="रिंग साइज">
                  <Input placeholder="रिंग साइज" maxLength={50} />
                </Form.Item>
                <Form.Item name="samaanGinti" label="समान की गिनती">
                  <Input placeholder="समान की गिनती" maxLength={100} />
                </Form.Item>
              </div>
            </Col>
            <Col xs={24}>
              <Form.Item name="marking" label="मार्किंग" style={{ marginBottom: 0, marginTop: 8 }}>
                <Checkbox.Group
                  options={MARKING_OPTIONS}
                  className="order-edit-marking-checkboxes"
                  style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 24px' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="parcelImages" label="पार्सल तस्वीर">
                <ProductImagesFormField
                  addButtonText="तस्वीर जोड़ें"
                  hintText="एक या अधिक तस्वीरें चुनें (अधिकतम ५)।"
                  className="order-edit-upload-large"
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
        </Form>

        <OrderSendToQcModal
          open={qcConfirmModalOpen}
          onCancel={() => {
            setQcConfirmModalOpen(false);
            setPendingSubmitValues(null);
          }}
          onConfirmYes={handleQcConfirmYes}
          onConfirmNo={handleQcConfirmNo}
          order={order}
          loading={submitLoading}
        />

        <OrderDispatchConfirmModal
          open={dispatchConfirmModalOpen}
          onCancel={() => {
            setDispatchConfirmModalOpen(false);
            setPendingSubmitValues(null);
          }}
          onConfirmYes={handleDispatchConfirmYes}
          onConfirmNo={handleDispatchConfirmNo}
          order={order}
          loading={submitLoading}
        />

        <OrderCompleteConfirmModal
          open={completeConfirmModalOpen}
          onCancel={() => {
            setCompleteConfirmModalOpen(false);
            setPendingSubmitValues(null);
          }}
          onConfirmYes={handleCompleteConfirmYes}
          onConfirmNo={handleCompleteConfirmNo}
          order={order}
          loading={submitLoading}
        />

        <Modal
          title="Send to Repair?"
          open={repairConfirmModalOpen}
          onOk={handleRepairConfirmYes}
          onCancel={handleRepairConfirmNo}
          okText="Yes, send to Repair"
          cancelText="No"
          confirmLoading={submitLoading}
        >
          <p>Are you sure you want to send this order to repair? The order status will change to <strong>Repair</strong>.</p>
        </Modal>

        <Card title={<span><MessageOutlined style={{ marginRight: 8 }} />Comments</span>} className="order-edit-comments-card">
          <div className="order-edit-chat">
            <div className="order-edit-chat-messages" ref={chatMessagesRef}>
              {order.comments && order.comments.length > 0 ? (
                order.comments.map((item, idx) => {
                  const isSystem = !item.created_by;
                  const name = item.created_by ? (item.created_by.name || item.created_by.email || '—') : 'Kosha';
                  const initial = (name !== '—' && name !== 'Kosha' ? name.charAt(0) : isSystem ? '' : name.charAt(0)).toUpperCase();
                  const isOwn = currentUser?.email && item.created_by?.email === currentUser.email;
                  return (
                    <div
                      key={item._id || `${item.created_at}-${idx}`}
                      className={`order-edit-chat-bubble-wrap ${isOwn ? 'order-edit-chat-bubble-own' : ''}`}
                    >
                      {!isOwn && (
                        <div className={`order-edit-chat-avatar ${isSystem ? 'order-edit-chat-avatar-kosha' : ''}`}>
                          {isSystem ? (
                            <img src={`${process.env.PUBLIC_URL || ''}/KOSHA-logo.png`} alt="Kosha" />
                          ) : (
                            initial
                          )}
                        </div>
                      )}
                      <div className="order-edit-chat-bubble">
                        <div className="order-edit-chat-bubble-header">
                          <span className="order-edit-chat-name">{isOwn ? 'You' : name}</span>
                          <span className="order-edit-chat-time">
                            {dayjs(item.created_at).isSame(dayjs(), 'day')
                              ? `Today ${dayjs(item.created_at).format('h:mm A')}`
                              : dayjs(item.created_at).format('MMM D, YYYY h:mm A')}
                          </span>
                        </div>
                        <div className="order-edit-chat-bubble-text" style={{ whiteSpace: 'pre-line' }}>{linkifyText(item.text)}</div>
                        {item.attachments && item.attachments.length > 0 && (
                          <CommentAttachments attachments={item.attachments} className="order-edit-chat-bubble-attachments" />
                        )}
                      </div>
                      {isOwn && <div className="order-edit-chat-avatar order-edit-chat-avatar-own">{initial}</div>}
                    </div>
                  );
                })
              ) : (
                <Empty description="No comments yet" image={Empty.PRESENTED_IMAGE_SIMPLE} className="order-edit-chat-empty" />
              )}
            </div>
            <div className="order-edit-comments-add">
              <Input.TextArea
                rows={2}
                placeholder="Type a message..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                className="order-edit-comment-input"
                autoSize={{ minRows: 2, maxRows: 4 }}
                maxLength={500}
              />
              <div className="order-edit-comment-footer">
                <CommentAttachmentInput
                  value={commentAttachments}
                  onChange={setCommentAttachments}
                  disabled={commentLoading}
                />
                <span className="order-edit-comment-count">{commentText.length}/500 characters</span>
                <Button
                  type="text"
                  shape="circle"
                  icon={<SendOutlined className="order-edit-comment-icon" />}
                  onClick={handleAddComment}
                  loading={commentLoading}
                  disabled={!commentText?.trim()}
                  className="order-edit-comment-add-btn"
                  aria-label="Add comment"
                />
              </div>
            </div>
          </div>
        </Card>
      </Space>
    </div>
  );
}
